import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import api, { fileUrl } from "@/lib/api";
import { rupee, effectivePrice } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ShoppingCart, ShoppingBasket, Minus, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { StarRating } from "@/components/StarRating";

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const img = fileUrl(product.image_id);
  const hasDiscount = product.discount_price && product.discount_price > 0;
  const soldOut = product.stock <= 0;
  const pct = hasDiscount ? Math.round(((product.price - product.discount_price) / product.price) * 100) : 0;
  const maxQty = Math.max(1, product.stock);

  const track = () => api.post(`/products/${product.id}/click`).catch(() => {});

  const onAdd = () => {
    if (soldOut) return;
    track();
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };
  const onBuy = () => {
    if (soldOut) return;
    track();
    addItem(product, qty);
    navigate("/cart");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.06, 0.4) }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white shadow-ambient transition-transform duration-300 hover:-translate-y-1 hover:shadow-lift"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/products/${product.id}`} className="relative block">
        <div className="aspect-[4/3] w-full overflow-hidden bg-cream-alt">
          {img ? (
            <img src={img} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink-muted">
              <ShoppingBasket className="h-12 w-12" />
            </div>
          )}
          {product.is_bestseller && !soldOut && (
            <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-navy px-2.5 py-1 text-[11px] font-bold text-white shadow" data-testid={`bestseller-badge-${product.id}`}>
              <Star className="h-3 w-3 fill-brand text-brand" /> Bestseller
            </span>
          )}
          {hasDiscount && !soldOut && (
            <span className="absolute right-3 top-3 rounded-full bg-brand px-3 py-1 text-xs font-bold text-ink shadow">{pct}% OFF</span>
          )}
          {soldOut && (
            <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm font-semibold uppercase tracking-widest text-ink">Sold Out</span>
          )}
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between text-xs text-ink-muted">
          <span className="font-medium text-sage">In stock ({product.stock})</span>
          <span>{product.unit}</span>
        </div>
        <Link to={`/products/${product.id}`}>
          <h3 className="mt-1 font-heading text-lg text-ink line-clamp-1 hover:text-brand">{product.name}</h3>
        </Link>
        <p className="mt-0.5 text-sm text-ink-muted line-clamp-1">{product.description || "Fresh & quality checked"}</p>
        {product.rating_count > 0 && (
          <div className="mt-1 flex items-center gap-1" data-testid={`card-rating-${product.id}`}>
            <StarRating value={product.rating_avg} size="h-3.5 w-3.5" />
            <span className="text-xs text-ink-muted">({product.rating_count})</span>
          </div>
        )}

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-xl font-bold text-ink">{rupee(effectivePrice(product))}</span>
          {hasDiscount && <span className="text-sm text-ink-muted line-through">{rupee(product.price)}</span>}
        </div>

        {!soldOut && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-full border border-border">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-8 w-8 items-center justify-center text-ink-soft hover:text-brand" data-testid={`card-qty-dec-${product.id}`} aria-label="Decrease">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-7 text-center text-sm font-semibold" data-testid={`card-qty-${product.id}`}>{qty}</span>
                <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="flex h-8 w-8 items-center justify-center text-ink-soft hover:text-brand" data-testid={`card-qty-inc-${product.id}`} aria-label="Increase">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <Button onClick={onAdd} data-testid={`add-to-cart-${product.id}`} className="flex-1 rounded-full bg-brand font-semibold text-ink hover:bg-brand-hover">
                <ShoppingCart className="mr-1.5 h-4 w-4" /> Add To Cart
              </Button>
            </div>
            <Button onClick={onBuy} data-testid={`buy-now-${product.id}`} className="w-full rounded-full bg-navy font-semibold text-white hover:bg-navy-hover">
              Buy Now
            </Button>
          </div>
        )}
        {soldOut && (
          <Button disabled className="mt-3 w-full rounded-full" variant="outline">Out of stock</Button>
        )}
      </div>
    </motion.div>
  );
}
